import InstanceApi from "@/services/apiClient.js";

import { mapGetters, mapActions } from "vuex";
import EventBus from "@/event-bus";


export default {
    
    /*-------------------------
        Datas
    ---------------------------*/
    data() {
        return {
            currentForm: {
                questions: [],
                name: '',
                answers: {}
            },
            currentQuestion: {
                index: 0,
                key: '',
                isAnswerValid: false,
                gtmDatas: {}
            },
            leadForm: {
                isNextStep: false,
                display: this.isLeadForm
            },
            displayErrorMessage: false,
            formInitialized: [],
        };
    },

    /*-------------------------
        Computed
    ---------------------------*/
    computed: {
        //Watch nested object
        currentIndexQuestion() {
            if(this.currentQuestion.index < this.currentForm.questions.length) return this.currentQuestion.index;
        },

        ...mapGetters({ 
            leadConfig: "getLeadConfig",
            dwellingForm: "getDwellingForm",
            dwellingAnswers: "getDwellingStepAnswers",
            aidsForm: "getAidsForm",
            aidsAnswers: "getAidsStepAnswers",
            interventionZipcode: "getInterventionZipcode",
            leadFormAnswers: "getLeadFormAnswers",
            gtmDatasEvents: "getGtmEventsDatas",
            currentStep: "getCurrentStep",
        }),
    },

    /*-------------------------
        Methods
    ---------------------------*/
    methods: {
        
        /**
         * Initialize currentForm (dwelling, aids)
         * @param {String} name 
         */
        _initForm(name) {          
            if(name == 'statutForm') {// Just last question (mcp_home_ownership)
                let form = [];
                form.push(this.aidsForm[this.aidsForm.length - 1])

                this.currentForm = {
                    questions: form,
                    answers: this.dwellingAnswers,
                    name,
                }
            } else {
                this.currentForm = {
                    questions: this[`${name}Form`],//from computed data
                    answers: this[`${name}Answers`],//from computed data
                    name,
                }
            }
            
        },

        /**
         * Set key from question
         */
        _setCurrentQuestionData() {
            const currentQuestion = this.currentForm.questions[this.currentQuestion.index];
            const gtmData = {
                name: currentQuestion?.gtm_datas?.gtm_eventName,
                step: currentQuestion?.gtm_datas?.gtm_step,
            }

            //Fecth answers options depending of previous responses
            this._fetchQuestionOptions(currentQuestion)

            this.currentQuestion.key = currentQuestion.key;

            this.currentQuestion.gtmDatas = gtmData;
        },

        /**
         * Check if question is overwritting(with same key) answer from parent question
         */
        _isSubQuestionOverwriting() {
            const currentQuestion = this.currentForm.questions[this.currentQuestion.index];
            const previousQuestion = this.currentForm.questions[this.currentQuestion.index - 1];

            return previousQuestion && previousQuestion.key === currentQuestion.key;
        },

        /**
         * Check if subQuestion with same key than parent question is answered
         */
        _isSubQuestionAnswered() {
            const currentQuestion = this.currentForm.questions[this.currentQuestion.index];
            const questionKeyAnswer = this.currentForm.answers[currentQuestion.key];
            
            return currentQuestion.options.find(option => option.value === questionKeyAnswer) ? true : false
        },

        /**
         * Fecth answers options depending of previous responses
         * @param {Object} currentQuestion 
         * @returns 
         */
        _fetchQuestionOptions(currentQuestion) {
            if(
                (currentQuestion.options && currentQuestion.options.length > 0) && !currentQuestion.needUpdate || 
                currentQuestion.input_type !== 'radio' 
            ) return;
                
            // Flag the questions to be updates each time
            currentQuestion.needUpdate = true;
            
            switch (currentQuestion.key) {
                case "mcp_tax_revenue":
                    const interventionZipcode = this.leadFormAnswers.postalcode;
                    const personsInHousehold = this.currentForm.answers.mcp_person_in_household;
                    this._getTaxRevenuOptions(personsInHousehold ,interventionZipcode)
                    break;
            
                default:
                    break;
            }
        },

        /**
         * Is current question already answered
         * @returns Boolean
         */
        _isQuestionAnswered() {
            //sub-questions with same key than parent
            if(this._isSubQuestionOverwriting()) return this._isSubQuestionAnswered();
           
            return this.currentForm.answers && this.currentForm.answers.hasOwnProperty(`${this.currentQuestion.key}`);
        },

        /**
         * Get user answer (if already answered)
         */
        _getCurrentAnswer() {
            if(this._isQuestionAnswered()) {
                const isParentSubQuestion =  this.currentForm.answers[`${this.currentQuestion.key}_parent`] && this.currentForm.questions[this.currentQuestion.index].hasSubQuestion
                const currentAnswer =   isParentSubQuestion ? 
                                            this.currentForm.answers[`${this.currentQuestion.key}_parent`] :
                                            this.currentForm.answers[`${this.currentQuestion.key}`];

               return currentAnswer; 
            } 
            return '';
        },

        /**
         * Toggle answer validity
         * @param {Boolean} value 
         */
        _isAnswerValid(value) {
            this.currentQuestion.isAnswerValid = value;
        },

        /**
         * Format answer
         * @param {String} key current question key
         * @param {String} answer 
         * @returns 
         */
        _formatAnswerBeforeSave(key, answer){
            const formatedAnswer = {
                key,
                answer,
                form: this.currentForm.name
            }

            return formatedAnswer;
        },

        /**
         * Validate and save answer
         * @param {String} key 
         * @param {String} answer 
         * @param {String} answerLabel
         */
        _saveAnswer(key, answerValue, answerLabel=null) {
            const answerDatas = this._formatAnswerBeforeSave(key, answerValue);

            this.currentQuestion.isAnswerValid = true;
            this.setAnswerDatas(answerDatas);
            
            //Save gtm-events-datas in store
            if(this.currentQuestion.gtmDatas.name) this.setGtmEventsDatas(this._formatGtmDatas(answerValue, answerLabel, this.currentQuestion.gtmDatas.name));
            
        },

        /**
         * If answer get subQuestion
         * @param {Object} subQuestion 
         * @param {Boolean} overwriteAnswer - if subquestion answer overwrite parent answer
         */
        _setSubQuestion(subQuestion, overwriteAnswer=false) {
            const isInForm = this.currentForm.questions.find(question => question.key === subQuestion.key) ? true : false;

            if(!isInForm || overwriteAnswer) {
                //If sub question is in last question before leadForm
                this._nextStepLeadForm(false)
                
                this.currentForm.questions[this.currentQuestion.index].hasSubQuestion = true;
                
                this.currentForm.questions.splice(
                    this.currentQuestion.index + 1,
                    0,
                    subQuestion
                )
            }         
        },

        /**
         * Remove subQuestion and answer when back on question
         * @returns 
         */
        _removeSubQuestion() {
            const parentQuestion = this.currentForm.questions[this.currentQuestion.index];
            const subQuestion = this.currentForm.questions[this.currentQuestion.index + 1]
            
            parentQuestion.hasSubQuestion = false

            //Remove sub answer
            delete this.currentForm.answers[subQuestion.key]

            //Remove sub question
            this.currentForm.questions.splice(
                this.currentQuestion.index + 1,
                1
            )
        },

        /**
         * Check agency eligibility
         * @param {String} postalCode 
         */
        async _isPostalCodeEligible(postalCode){
            const productCategory = this.leadConfig.category;
            const url =`mcp/agency/get?category=${productCategory}&zipcode=${postalCode}&out_of_area_agency=1`
            let isEligible = false;

            await InstanceApi.get(url)
                .then(response => {
                    if (response.data instanceof Object) {

                        if(!response.data.defaultAgency){
                            isEligible = true
                        }
                        //Save agency id to send
                        this.setAnswerDatas({key:'mcp_agency', answer:response.data.id, form:'saving'});
                    } 
                    
                    if(!isEligible) {
                        EventBus.$emit("simulator-page::exit", "zipcode_failure");
                    }
                })
                .catch((e) => {
                    console.error(e)
            });

            return isEligible;
        },

        /**
         * Push a new question next current
         * @param {Object} newQuestion 
         */
        _pushNextNewQuestions(newQuestion) {
            let newQuestionPosition = this.currentQuestion?.index + 1;

            this.currentForm?.questions?.splice(newQuestionPosition, 0, newQuestion)
        },

        /**
         * Show Lead form next step
         * @param {Boolean} value 
         */
        _nextStepLeadForm(value) {
            this.leadForm.isNextStep = value;
        },

        /**
         * handle ends of each forms
         * @param {Boolean} isLastQuestion 
         * @returns 
         */
        _handleEndForms(isLastQuestion) {
            if(!isLastQuestion) return

            switch (this.currentForm.name) {
                case "dwelling":
                    this.leadForm.display = true;
                    break;
                
                case "aids":
                    EventBus.$emit("simulator-page::resume");
                    EventBus.$emit("stepAction::validate", 'aids');
                    break;
                
                case "statutForm":
                    EventBus.$emit('stepAction::current', 'final_step');
                    EventBus.$emit("simulator-page::resume");
                    break;
            
                default:
                    break;
            }
        },

        /**
         * Handle length of dwelling form depending on answers
         * @param {String} question - question key
         * @param {String} question - answer value
         */
        _handleOptionalDewellingQuestions(question, answer) {
            const isOverallRenovationQuestion = question ===  'mcp_overall_renovation';
            const isHouseAreaQuestion = question === "mcp_housing_area";
            const isBillAmountInformed = this?.currentForm?.answers?.mcp_energy_bill_amount !== '';
            let skipDwellingEndForm = false;

            //Validate dwelling step and go to saving page
            if(isOverallRenovationQuestion) {
                EventBus.$emit('stepAction::validate', 'dwelling');
                EventBus.$emit('stepAction::current', 'save_energy');
                skipDwellingEndForm = true;
                return skipDwellingEndForm;
            }

            //Skip dwellings infos part of form
            if(isHouseAreaQuestion && isBillAmountInformed) {
                skipDwellingEndForm = true;
                return skipDwellingEndForm;
                
            }

            return skipDwellingEndForm;
        },

        /**
         * Handle Checkbox exit answer Options
         * @param {String} question 
         * @param {any} answer 
         */
        _handleExitOptionAnswer(question, answer) { 
            let currentQuestion = this.currentForm?.questions?.find(questionForm => questionForm.key === question);        
            let currentAnswer = currentQuestion?.options?.find(answerCurr => answerCurr.value === answer);

            //  Handle SubQuestion - 
            //  ToDo -improve
            if(!currentAnswer) {
                currentQuestion.options.forEach(option => {
                    option?.sub_question?.options.forEach(sub_option => {
                        if(sub_option.value == answer) currentAnswer = sub_option;
                    })
                })
            }

            if(currentAnswer && currentAnswer.exit_option) {
                let exitStatus = currentAnswer.exit_status ?? 'aids_failure';
               
                EventBus.$emit("simulator-page::exit", exitStatus); 
            }
        },

        /**
         * Get the answers option for mcp_tax_revenue Questions
         * @param {String} personsInHousehold 
         * @param {String} interventionCode 
         */
        async _getTaxRevenuOptions(personsInHousehold, interventionCode) {
            const url = `simulateur-avance/steps/getIncomeCeilings/?mcp_person_in_household=${personsInHousehold}&mcp_intervention_code=${interventionCode}`
            const taxQuestion = this.currentForm.questions.find(question => question.key === 'mcp_tax_revenue');
            const newOptions = []
            
            await InstanceApi.get(url)
                .then(response => {               
                    const answerOptions = response.data;

                    Object.values(answerOptions).forEach(option => {
                        newOptions.push(option);

                    })
                    
                    taxQuestion.options = newOptions;
                })
                .catch((e) => {
                    console.error(e)
            });
        },

        /**
         * auto scroll to first error message
         */
        _scrollToErrorMessage() {
            window.scrollTo(0, document.querySelectorAll('.form-error')[0].offsetTop)
        },

        /**
         * Map Actions
         */
        ...mapActions([
            "setAnswerDatas",
            "setPreviousQuestion",
            "setGtmEventsDatas",
            "setMcpDpe"
        ]),


        /**
         * Format gtm-datas to save in store
         * @param {String} answerValue 
         * @param {String} answerLabel 
         * @param {String} gtmEventName 
         * @returns 
         */
        _formatGtmDatas(answerValue, answerLabel, gtmEventName) {
            let currentQuestionsDatas = this.currentForm.questions.find(question => question.gtm_datas.gtm_eventName === gtmEventName );
            const answerValueFormated = this._formatGtmAnswerValue(currentQuestionsDatas, answerValue);        
            let datas = {};

            if(currentQuestionsDatas) {
                
                datas = {
                    name: currentQuestionsDatas?.gtm_datas?.gtm_eventName ?? '',
                    step: currentQuestionsDatas?.gtm_datas?.gtm_step ?? '',
                    value: answerValueFormated ?? '',
                }

                //Exceptions
                if(datas.name == "tenant") datas.name = 'is_tenant'

            }

            return datas;
        },

        /**
         * 
         * @param {Object} questionDatas 
         * @param {String} answerValue 
         * @returns 
         */
        _formatGtmAnswerValue(questionDatas, answerValue) {
            // Number value
            if(questionDatas?.input_type === "range" || questionDatas.input_type === "number") {
                if(!answerValue) return 0;
                return parseInt(answerValue);
            }

            //Boolean value
            const boolValuesArray = ['yes', 'oui', 'no', 'non']
            if(boolValuesArray.includes(answerValue.toLowerCase())) {
                if(answerValue.toLowerCase() == 'yes' || answerValue.toLowerCase() == 'oui') return true;
                if(answerValue.toLowerCase() == 'no' || answerValue.toLowerCase() == 'non') return false;
            }

            //Label response (not value)
            const questionsToChange = ['heating_type', 'heating_subtype'];
            if(questionsToChange.includes(questionDatas.gtm_datas.gtm_eventName)) {
                let answer = questionDatas.options.find(option => option.value === answerValue)
                return answer.label;
            }

            return answerValue;
        },

        /**
         * Push gtm-events in datalayer
         * @param {Object} currentStepEventDatas 
         */
        _pushGtmEvents(currentStepEventDatas) {
            let previousStepsEventsDatas = this.gtmDatasEvents;
            let currentEventStep = currentStepEventDatas.step ? `step${currentStepEventDatas.step}_` : '';
            let currentEventName = currentStepEventDatas.name;
            let now = new Date();
            const isLeadFormEvent = currentStepEventDatas.name === 'personal_info';
            const urlParams         = new URLSearchParams(window.location.search);
            const ctaSourceParam    = urlParams.get('cta_source');
            

            let customDatas = {
                page_type: "simulateur",
                funnel_type:"simulateur2",
                cta_source: ctaSourceParam,
                timestamp: now.toLocaleString('sv-SE') + '.' + now.getMilliseconds(),
            }

            //Aditional datas
            if(currentStepEventDatas.additonalDatas) {
                Object.entries(currentStepEventDatas.additonalDatas).forEach(([key, value]) => {
                    customDatas[key] = value;
                });
            }

            //Lead form event
            if(isLeadFormEvent) {
                customDatas['postal_code'] = this.leadFormAnswers.postalcode;
            }

            //Push previous datas until current step (no more if user go back)
            Object.entries(previousStepsEventsDatas).forEach(([key, data]) => {
                if(data.step <= currentStepEventDatas.step)customDatas[`${data.name}`] = data.value;
            }) 

            //Push
            if(window.McpGtm) {
                McpGtm.pushCustomDataLayer(
                    `simulator2_${currentEventStep}${currentEventName}`,
                    {},
                    customDatas
                )
            }
        }
    },

    /*-------------------------
        Watch
    ---------------------------*/
    watch: {
        currentIndexQuestion() {
            this._setCurrentQuestionData();
            this._isAnswerValid(this._isQuestionAnswered())
        },
    },
};
